import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/api";
import { usePatientAuth } from "@/contexts/PatientAuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import LanguageToggle from "@/components/LanguageToggle";
import { ArrowLeft, HeartPulse, Smartphone, Lock, KeyRound } from "lucide-react";
import { toast } from "sonner";
import TermsCheckbox from "@/components/TermsCheckbox";
import ABDMABHARegistrationModal from "@/components/ipd/ABDMABHARegistrationModal";

export default function PatientLogin() {
  const navigate = useNavigate();
  const { setSession } = usePatientAuth();
  useI18n();
  
  // Step: mobile | otp | password-setup | password-login | forgot-password-otp | forgot-password-reset
  const [step, setStep] = useState("mobile");
  const [mobile, setMobile] = useState("");
  const [name, setName] = useState("");
  const [otp, setOtp] = useState("");
  const [demoOtp, setDemoOtp] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showAbha, setShowAbha] = useState(false);
  const [isFirstLogin, setIsFirstLogin] = useState(false);
  const [hasPassword, setHasPassword] = useState(false);

  // ========================================================================
  // STEP 1: Request OTP (Mobile Number)
  // ========================================================================
  const requestOtp = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/auth/patient/otp/request`, { mobile });
      setDemoOtp(data.demo_otp || "");
      toast.success(data.demo_otp ? `OTP sent (demo: ${data.demo_otp})` : "OTP sent to your mobile");
      setStep("otp");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ========================================================================
  // STEP 2: Verify OTP - Detect first login vs existing user
  // ========================================================================
  const verifyOtp = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/auth/patient/otp/verify`, {
        mobile,
        otp,
        name: name || undefined,
      });

      // Check response to determine next step
      if (data.first_login === true) {
        // First-time user - needs to set password
        setIsFirstLogin(true);
        setHasPassword(false);
        setStep("password-setup");
        toast.success("OTP verified! Now set your password");
      } else if (data.has_password === true) {
        // Existing user with password - needs to enter password
        setIsFirstLogin(false);
        setHasPassword(true);
        setStep("password-login");
        toast.info("Please enter your password");
      } else {
        toast.error("Unexpected response. Please try again.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ========================================================================
  // STEP 3A: Setup Password (First-time login)
  // ========================================================================
  const setupPassword = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (password.length < 6) {
        throw new Error("Password must be at least 6 characters");
      }
      if (password !== passwordConfirm) {
        throw new Error("Passwords don't match");
      }

      const response = await axios.post(
        `${API}/auth/patient/setup-password`,
        { mobile, password, password_confirm: passwordConfirm },
        { withCredentials: true }  // Include cookies
      );

      // Set session with returned user data
      setSession(response.data);
      toast.success("Password set! Logging you in...");
      navigate("/patient/home");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ========================================================================
  // STEP 3B: Password Login (Existing user)
  // ========================================================================
  const passwordLogin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/auth/patient/password-login`,
        { mobile, password },
        { withCredentials: true }  // Include cookies
      );

      // Set session with returned user data
      setSession(response.data);
      toast.success("Welcome back! 🎉");
      navigate("/patient/home");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ========================================================================
  // FORGOT PASSWORD - Step 1: Request OTP
  // ========================================================================
  const requestForgotPasswordOtp = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/patient/forgot-password/request`, { mobile });
      toast.success("OTP sent to your mobile");
      setStep("forgot-password-otp");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ========================================================================
  // FORGOT PASSWORD - Step 2: Verify OTP
  // ========================================================================
  const verifyForgotPasswordOtp = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/patient/forgot-password/verify`, { mobile, otp });
      toast.success("OTP verified!");
      setStep("forgot-password-reset");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ========================================================================
  // FORGOT PASSWORD - Step 3: Reset Password
  // ========================================================================
  const resetPassword = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (password.length < 6) {
        throw new Error("Password must be at least 6 characters");
      }
      if (password !== passwordConfirm) {
        throw new Error("Passwords don't match");
      }

      const response = await axios.post(
        `${API}/auth/patient/reset-password`,
        { mobile, password, password_confirm: passwordConfirm },
        { withCredentials: true }  // Include cookies
      );

      // Set session with returned user data
      setSession(response.data);
      toast.success("Password reset! Logging you in...");
      navigate("/patient/home");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="patient-login-page">
      <header className="px-4 sm:px-8 py-4 flex items-center justify-between bg-white border-b">
        <Link to="/patient" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900" data-testid="back-patient-portal">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
        <LanguageToggle />
      </header>

      <main className="flex-1 flex items-center justify-center px-4 pb-12">
        <Card className="w-full max-w-md p-7 medical-card">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-sky-500 to-emerald-500 flex items-center justify-center">
              <HeartPulse className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-semibold text-slate-900">Patient Login</h1>
              <p className="text-xs text-slate-500">Secure access to your health records</p>
            </div>
          </div>

          {/* ================================================================
              STEP 1: Mobile Number
              ================================================================ */}
          {step === "mobile" && (
            <form onSubmit={requestOtp} className="space-y-4">
              <div>
                <Label>Your name (optional)</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="patient-login-name"
                  placeholder="Asha Verma"
                />
              </div>
              <div>
                <Label>Mobile number</Label>
                <Input
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  required
                  minLength={10}
                  maxLength={10}
                  inputMode="numeric"
                  data-testid="patient-login-mobile"
                  placeholder="10-digit mobile"
                />
              </div>
              <Button type="submit" disabled={submitting} className="w-full bg-sky-500 hover:bg-sky-600 text-white gap-2" data-testid="patient-otp-request-btn">
                <Smartphone className="w-4 h-4" /> {submitting ? "Sending..." : "Send OTP"}
              </Button>
              <button type="button" onClick={() => setShowAbha(true)}
                      className="w-full text-xs text-blue-700 hover:underline text-center flex items-center justify-center gap-1"
                      data-testid="patient-login-create-abha">
                Don&apos;t have an ABHA ID? Create one
              </button>
            </form>
          )}

          {/* ================================================================
              STEP 2: OTP Verification
              ================================================================ */}
          {step === "otp" && (
            <form onSubmit={verifyOtp} className="space-y-4">
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-700">
                OTP sent to <span className="font-mono">{mobile}</span>
                {demoOtp && (<><br /><span className="text-slate-500">Demo OTP (no SMS provider): <span className="font-mono font-semibold">{demoOtp}</span></span></>)}
              </div>
              <div>
                <Label>Enter OTP</Label>
                <Input
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  required
                  minLength={4}
                  maxLength={6}
                  inputMode="numeric"
                  data-testid="patient-otp-input"
                  placeholder="6-digit OTP"
                  className="text-center tracking-[0.4em] font-mono text-lg"
                />
              </div>
              <TermsCheckbox
                checked={acceptedTerms}
                onChange={setAcceptedTerms}
                testid="patient-terms-checkbox"
              />
              <Button type="submit" disabled={submitting || !acceptedTerms} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="patient-otp-verify-btn">
                {submitting ? "Verifying..." : "Verify OTP"}
              </Button>
              <button type="button" onClick={() => { setStep("mobile"); setOtp(""); }} className="text-xs text-slate-500 hover:underline w-full text-center">
                Change mobile number
              </button>
            </form>
          )}

          {/* ================================================================
              STEP 3A: Password Setup (First-time)
              ================================================================ */}
          {step === "password-setup" && (
            <form onSubmit={setupPassword} className="space-y-4">
              <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-xs text-blue-700">
                Welcome! Set your password to secure your account
              </div>
              <div>
                <Label>Password (min. 6 characters)</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  data-testid="patient-password"
                  placeholder="Enter password"
                />
              </div>
              <div>
                <Label>Confirm Password</Label>
                <Input
                  type="password"
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  required
                  minLength={6}
                  data-testid="patient-password-confirm"
                  placeholder="Confirm password"
                />
              </div>
              <Button type="submit" disabled={submitting} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white gap-2">
                <Lock className="w-4 h-4" /> {submitting ? "Setting up..." : "Set Password"}
              </Button>
            </form>
          )}

          {/* ================================================================
              STEP 3B: Password Login (Existing user)
              ================================================================ */}
          {step === "password-login" && (
            <form onSubmit={passwordLogin} className="space-y-4">
              <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-xs text-blue-700">
                Welcome back! Enter your password
              </div>
              <div>
                <Label>Password</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="patient-password"
                  placeholder="Enter password"
                />
              </div>
              <Button type="submit" disabled={submitting} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white gap-2">
                <Lock className="w-4 h-4" /> {submitting ? "Logging in..." : "Login"}
              </Button>
              <button type="button" onClick={() => { setStep("mobile"); setMobile(""); setPassword(""); setOtp(""); }}
                      className="text-xs text-blue-600 hover:underline w-full text-center">
                Try different mobile number
              </button>
              <button type="button" onClick={() => { setStep("mobile"); setOtp(""); setPassword(""); setPasswordConfirm(""); }}
                      className="text-xs text-slate-500 hover:underline w-full text-center">
                Forgot password?
              </button>
            </form>
          )}

          {/* ================================================================
              FORGOT PASSWORD - Step 1: Mobile for reset
              ================================================================ */}
          {step === "forgot-password-mobile" && (
            <form onSubmit={requestForgotPasswordOtp} className="space-y-4">
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-700">
                Enter the mobile number linked to your account
              </div>
              <div>
                <Label>Mobile number</Label>
                <Input
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  required
                  minLength={10}
                  maxLength={10}
                  inputMode="numeric"
                  placeholder="10-digit mobile"
                />
              </div>
              <Button type="submit" disabled={submitting} className="w-full bg-amber-500 hover:bg-amber-600 text-white">
                {submitting ? "Sending..." : "Send Reset OTP"}
              </Button>
              <button type="button" onClick={() => setStep("mobile")} className="text-xs text-slate-500 hover:underline w-full text-center">
                Back to login
              </button>
            </form>
          )}

          {/* ================================================================
              FORGOT PASSWORD - Step 2: Verify OTP
              ================================================================ */}
          {step === "forgot-password-otp" && (
            <form onSubmit={verifyForgotPasswordOtp} className="space-y-4">
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-700">
                Enter the OTP sent to {mobile}
              </div>
              <div>
                <Label>OTP</Label>
                <Input
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  required
                  minLength={4}
                  maxLength={6}
                  inputMode="numeric"
                  placeholder="6-digit OTP"
                  className="text-center tracking-[0.4em] font-mono text-lg"
                />
              </div>
              <Button type="submit" disabled={submitting} className="w-full bg-amber-500 hover:bg-amber-600 text-white">
                {submitting ? "Verifying..." : "Verify OTP"}
              </Button>
            </form>
          )}

          {/* ================================================================
              FORGOT PASSWORD - Step 3: Reset Password
              ================================================================ */}
          {step === "forgot-password-reset" && (
            <form onSubmit={resetPassword} className="space-y-4">
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-700">
                Set your new password
              </div>
              <div>
                <Label>New Password (min. 6 characters)</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  placeholder="Enter new password"
                />
              </div>
              <div>
                <Label>Confirm Password</Label>
                <Input
                  type="password"
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  required
                  minLength={6}
                  placeholder="Confirm new password"
                />
              </div>
              <Button type="submit" disabled={submitting} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white gap-2">
                <KeyRound className="w-4 h-4" /> {submitting ? "Resetting..." : "Reset Password"}
              </Button>
            </form>
          )}
        </Card>
      </main>
      <ABDMABHARegistrationModal open={showAbha} onClose={() => setShowAbha(false)} />
    </div>
  );
}
